import {
    Button,
    Card,
    InfiniteScroll,
    Spinner,
    SpotIllustration,
    StatusInfo,
} from '@george-labs.com/design-system';
import { type FC, useEffect } from 'react';
import { SPOT_ILLUSTRATION } from '@george-labs.com/george-assets/illustrations';

import { t } from '@luna/i18n';

import type { BankMessage } from '../BankMessagesTypes';
import { BankMessageList } from './BankMessageList';
import { trackOpenBankMessages } from '../BankMessagesTrackingService';

interface Props {
    isFirstPageLoading: boolean;
    hasError: boolean;
    hasNextPage: boolean;
    messages: BankMessage[];
    showMessageSender?: boolean;
    showMessageIcon?: boolean;
    messageAbsolutePath: string;
    fetchMore(): void;
    refetch(): void;
}

export const BankMessagesOverview: FC<Props> = ({
    isFirstPageLoading,
    hasError,
    messages,
    hasNextPage,
    showMessageSender = false,
    showMessageIcon = false,
    messageAbsolutePath,
    fetchMore,
    refetch,
}) => {
    useEffect(() => {
        trackOpenBankMessages();
    }, []);

    if (isFirstPageLoading) {
        return <Spinner center />;
    }

    if (hasError) {
        return (
            <Card>
                <StatusInfo
                    preferIllustration
                    data-cy="bank-messages-error"
                    variant={StatusInfo.VARIANT.ERROR}
                    title={t('bankMessages.overview.fetchMessagesErrorTitle')}
                    buttons={[
                        <Button key="refetch" data-cy="reload-bank-messages" onClick={refetch}>
                            {t('bankMessages.overview.fetchMessagesErrorRefetchBtn')}
                        </Button>,
                    ]}
                >
                    {t('bankMessages.overview.fetchMessagesErrorDescription')}
                </StatusInfo>
            </Card>
        );
    }

    if (messages.length === 0) {
        return (
            <Card>
                <StatusInfo
                    data-cy="empty-bank-messages"
                    illustration={
                        <SpotIllustration
                            ornamental
                            illustration={SPOT_ILLUSTRATION.PHONE_NOTIFICATION}
                        />
                    }
                    title={t('bankMessages.overview.noMessagesTitle')}
                >
                    {t('bankMessages.overview.noMessagesDescription')}
                </StatusInfo>
            </Card>
        );
    }

    return (
        <InfiniteScroll active={hasNextPage} onEndOfScreen={fetchMore}>
            <BankMessageList
                showMessageSender={showMessageSender}
                showMessageIcon={showMessageIcon}
                messageAbsolutePath={messageAbsolutePath}
                messages={messages}
            />
            {hasNextPage && <Spinner center className="mb-2" />}
        </InfiniteScroll>
    );
};
